import React, { useMemo, useState } from "react";
import { UserX, ShieldOff, History, AlertTriangle } from "lucide-react";

const statusLabel = (p) => (p.employment_status === "former" ? "Former" : p.active_status ? "Active" : "Inactive");

export default function FormerStaffModal({ people, source, onCancel, onConfirm, busy }) {
  const eligible = useMemo(() => people.filter((p) => p.matched !== false && !p.already_former && !p.blocked), [people]);
  const [selected, setSelected] = useState(() => new Set(eligible.map((p) => p.user_id)));
  const [reason, setReason] = useState("");
  const [endDate, setEndDate] = useState("");
  const [ack, setAck] = useState(false);

  const toggle = (id) => { setAck(false); setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const allOn = selected.size === eligible.length && eligible.length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => !busy && onCancel()}>
      <div className="nv-card w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="former-staff-modal">
        <div className="p-6 pb-4 border-b border-slate-100">
          <h2 className="text-h2 inline-flex items-center gap-2"><UserX size={18} className="text-rose-700" /> Mark as Former Staff</h2>
          <p className="text-sm text-slate-500 mt-1">{source ? `Pre-loaded from ${source}. ` : ""}Untick anyone who should stay. Nothing is saved until you confirm.</p>
          <div className="mt-3 grid sm:grid-cols-3 gap-2 text-[12.5px]">
            <div className="rounded-md bg-rose-50 border border-rose-100 px-3 py-2 text-rose-800 flex gap-2"><ShieldOff size={14} className="shrink-0 mt-0.5" />Login disabled, active sessions signed out, removed from Staff Directory and scoring pickers.</div>
            <div className="rounded-md bg-emerald-50 border border-emerald-100 px-3 py-2 text-emerald-800 flex gap-2"><History size={14} className="shrink-0 mt-0.5" />KPI scores, QA audits and audit history stay exactly as they are, under their name.</div>
            <div className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-amber-800 flex gap-2"><AlertTriangle size={14} className="shrink-0 mt-0.5" />Future spreadsheet imports flag them instead of reactivating. Reversible via Reinstate.</div>
          </div>
        </div>

        <div className="overflow-auto px-6 py-3 flex-1">
          <table className="w-full text-sm" data-testid="former-staff-table">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="py-2 pr-2 text-left"><input type="checkbox" checked={allOn} onChange={(e) => { setAck(false); setSelected(new Set(e.target.checked ? eligible.map((p) => p.user_id) : [])); }} data-testid="former-select-all" aria-label="Select all" /></th>
                <th className="py-2 text-left">Name</th><th className="py-2 text-left">Email</th><th className="py-2 text-left">Role</th><th className="py-2 text-left">Home</th><th className="py-2 text-left">Now</th>
              </tr>
            </thead>
            <tbody>
              {people.map((p, idx) => {
                const canPick = p.matched !== false && !p.already_former && !p.blocked;
                const key = p.user_id || `${p.email}-${idx}`;
                return (
                  <tr key={key} className={`border-t border-slate-100 ${canPick ? "" : "opacity-60"}`} data-testid={`former-row-${key}`}>
                    <td className="py-2 pr-2"><input type="checkbox" disabled={!canPick} checked={canPick && selected.has(p.user_id)} onChange={() => toggle(p.user_id)} data-testid={`former-check-${key}`} /></td>
                    <td className="py-2 font-medium text-slate-800">{p.full_name}</td>
                    <td className="py-2 text-slate-600 text-xs">{p.email}</td>
                    <td className="py-2 text-slate-600">{p.access_role || "—"}</td>
                    <td className="py-2 text-slate-600">{(p.assigned_homes && p.assigned_homes.length > 1) ? p.assigned_homes.join(" + ") : (p.assigned_home || p.home || "—")}</td>
                    <td className="py-2 text-xs">
                      {p.matched === false ? <span className="text-slate-500">Not in directory</span>
                        : p.already_former ? <span className="text-rose-700">Already Former</span>
                        : p.blocked ? <span className="text-amber-700" title={p.blocked}>Blocked</span>
                        : <span className={p.active_status ? "text-emerald-700" : "text-slate-500"}>{statusLabel(p)}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="p-6 pt-4 border-t border-slate-100 space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-small-caps">Reason (optional)</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Left the organization" className="mt-1 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="former-reason-input" /></label>
            <label className="block"><span className="text-small-caps">Last day (optional)</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-full text-sm rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="former-end-date-input" /></label>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1" data-testid="former-ack-checkbox" />
            I understand these {selected.size} {selected.size === 1 ? "person" : "people"} will lose access immediately. Their records are kept and this can be reversed with Reinstate.
          </label>
          <div className="flex justify-end gap-2">
            <button onClick={onCancel} disabled={busy} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" data-testid="former-cancel-btn">Cancel</button>
            <button onClick={() => onConfirm({ user_ids: [...selected], reason, end_date: endDate })} disabled={busy || !ack || selected.size === 0}
              className="px-4 py-2 rounded-md text-sm font-semibold bg-rose-700 hover:bg-rose-800 text-white disabled:opacity-50" data-testid="former-confirm-btn">
              {busy ? "Saving…" : `Mark ${selected.size} as Former Staff`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
